import { useState } from 'react';
import useApiAuthorization from './useApiAuthorization';

const apiBase = `${process.env.REACT_APP_API_BASE_URI}/`;

function useApi (url) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const apiAuthorization = useApiAuthorization();

    const request = async (params, token, options) => {
        options = options || {};
        setLoading(true);

        if (!token) {
            token = await apiAuthorization.getAccessToken();
        }

        try {
            if (token) {
                options.headers = options.headers || {};
                options.headers.Authorization = 'Bearer ' + token;
            }

            let queryString = '';

            if (params && typeof params === 'object') {
                queryString = toQueryString(params);
            }

            const [responseData, error, response] = await doFetch(
                apiBase + url + queryString,
                options
            );
            setData(responseData);
            setResponse(response);
            setError(error);
            return { responseData, response, error };
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const get = async (params, token, options) => {
        const {responseData} = await request(params, token, options);
        return responseData;
    };

    const getWithResponse = async (params, token, options) => {
        return await request(params, token, options);
    };

    const post = async (body, options, token, withResponse) => {
        options = options || {};
        options.method = 'POST';
        options.body = body;
        options.headers = options.headers || {};
        options.responseBody = options.responseBody || 'json';
        withResponse = withResponse || false;

        const {responseData, response} = await request(null, token, options);
        return withResponse ? { responseData, response } : responseData;
    };

    const jsonPost = async (body, options, token, withResponse) => {
        return await post(JSON.stringify(body), {
            headers: {
                'Content-Type': 'application/json'
            }
        }, token, withResponse);
    }

    const setUrl = (uri) => {
        url = uri;
    }

    const doFetch = async (url, options) => {
        let responseData = null;
        let error = false;
        const response = await fetch(url, options);

        try {
            responseData = options.responseBody === 'blob' && response.ok ? await response.blob() : await response.json();
        } catch(err) {

        }

        if (!response.ok) {
            error = fetchError(response, responseData);
            responseData = null;
        }

        return [responseData, error, response];
    };

    const fetchError = (response, responseData) => {
        return {
            response: response,
            status: response.status,
            message: response.statusText,
            title: responseData ? responseData.title : null,
            traceId: responseData ? responseData.traceId : null,
            detail : responseData ? responseData.detail : response.statusText
        }
    }

    const toQueryString = (params) => {
        return '?' + createSearchParams(params);
    };

    const createSearchParams = (params) => {
        return new URLSearchParams(
            Object.entries(params).flatMap(
                ([key, values]) => Array.isArray(values)
                    ? values.map((value) => [key, value])
                    : [[key, values]]
            )
        );
    }

    return {
        data,
        error,
        loading,
        response,
        get,
        getWithResponse,
        post,
        jsonPost,
        setUrl
    };
}

export default useApi;